import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Gallery, GalleryComponent, GalleryItem, GalleryState, ImageItem } from 'ng-gallery';
import { Project, Image } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import { filter, forkJoin, zip } from 'rxjs';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';

@Component({
  selector: 'project-panel-images',
  templateUrl: './images.component.html',
  styleUrls: ['./images.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsImagesComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  private galleryRef = this.gallery.ref('photoGallery');
  public galleryImages: GalleryItem[];
  public loading: boolean = false;
  public allowedFormats = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

  constructor(
    protected readonly changeDetectorRef: ChangeDetectorRef,
    private readonly projectsService: ProjectsService,
    private readonly gallery: Gallery,
    private readonly snackBar: MatSnackBar,
    private readonly confirmService: ConfirmService,
  ) {
    super(changeDetectorRef);
  }

  ngOnInit() {
    this.setTitleFieldDef();
    this.fetchProjects();
    this.panelColor = this.panelDef?.color;
  }

  private setTitleFieldDef(): void {
    this.titleFieldDef = {
      name: 'images.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };
  }

  private fetchProjects(): void {
    this.projectsService.project$.pipe(untilComponentDestroyed(this)).subscribe((project: Project) => {
      if (project) {
        this.project = project;
      }

      if (this.project) {
        this.loadImages();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private loadImages(): void {
    if (this.project) {
      this.galleryImages =
        this.project.images
          .filter((image: Image) => image?.list_image_url && image?.original_image_url)
          .map((image: Image) => new ImageItem({ src: image.original_image_url, thumb: image.list_image_url })) ?? [];

      this.galleryRef.load(this.galleryImages);
    }
  }

  public onFileSelected(event: Event & { target: { files: File[] } }): void {
    const files = Array.from(event.target.files);

    if (files.some(({ type }) => this.allowedFormats.indexOf(type) === -1)) {
      this.snackBar.open('Invalid file format. Please upload a JPEG, PNG, GIF, or PDF file.', '', {
        panelClass: 'error-snackbar',
        duration: 3000,
      });
      return;
    }

    this.loading = true;

    zip(forkJoin(files.map((file) => this.projectsService.uploadImage(file))))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.loading = false;
        this.snackBar.open('Succesfully uploaded image.', '', {
          duration: 3000,
        });
      });
  }

  public deletePhoto(gallery: GalleryComponent): void {
    let galleryState: GalleryState;

    gallery.galleryRef.state.pipe(untilComponentDestroyed(this)).subscribe((state) => {
      galleryState = state;
    });

    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you want to delete the image ${
          this.project.images[galleryState.currIndex].attachment_file_name
        }?`,
      })
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this.projectsService
          .deleteImage(this.project.images[galleryState.currIndex].id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            gallery.reset();
            this.snackBar.open('Succesfully deleted image.', '', {
              duration: 3000,
            });
            this.loadImages();
          });
      });

    return;
  }
}
